import { CURRENCY_DECIMALS, DEFAULT_CURRENCY_DECIMALS, convertMoney } from '@theme/money-formatting';
import { findVariantBySize, readVariants } from '@theme/ss-utils';
import { decodeEntities, productHandleFromUrl } from '@theme/utilities';

/**
 * Blotout EdgeTag events the theme fires for itself.
 *
 * The app embed tags a page view on load, but swapping print re-renders the product in place with no
 * navigation, so every print after the first would go untagged.
 *
 * @see https://docs.edgetag.io/overview/standard-events#viewcontent
 */

/**
 * The print selector's pick event. The name rather than `SsPrintSwatches.swatchUpdate`, so this file
 * loads on every page without pulling the selector's module onto pages that have none.
 */
const PRINT_SWATCH_UPDATE = 'ss-print-swatches:update';

/**
 * Theme roles that are not the live theme, so a payload is only logged where someone is working.
 * Opted out of by role rather than in on `main`: `Shopify.theme.role` is deprecated and its values
 * are subject to change, and an unfamiliar one should log nothing to customers.
 */
const DEBUG_ROLES = new Set(['development', 'unpublished', 'demo']);

/** EdgeTag's content type for one garment, as against a `product_group`. */
const CONTENT_TYPE = 'product';

/**
 * Send an event to EdgeTag. A no-op where the Blotout app embed is off, which is every environment it
 * has not been enabled in.
 *
 * @param {string} name
 * @param {object} data
 */
const tagEvent = (name, data) => {
  const { edgetag } = /** @type {any} */ (window);

  // Logged before the guard, so a theme without the app embed still shows what it would have sent.
  if (DEBUG_ROLES.has(/** @type {any} */ (window).Shopify?.theme?.role)) {
    console.debug('[edgetag]', name, data, typeof edgetag === 'function' ? '' : '(app embed off, not sent)');
  }

  if (typeof edgetag !== 'function') return;

  edgetag('tag', name, data);
};

/**
 * The size the selector asked each product type about. A print that is several garments is a size per
 * garment — a boxer in L beside a thong in M — and `data-matching` is what those were queried with.
 *
 * @param {HTMLElement} selector
 * @returns {Map<string, string>}
 */
const readSizesByType = (selector) => {
  try {
    return new Map(
      JSON.parse(selector.dataset.matching ?? '[]').map((/** @type {any} */ selection) => [
        selection.productType,
        selection.size,
      ]),
    );
  } catch {
    // the selector is about one garment, which data-size names
    return new Map();
  }
};

/**
 * The Ajax product JSON per handle, so a print looked at twice costs one request. Holds the promise
 * rather than the product, which dedupes a hover and the click behind it.
 *
 * @type {Map<string, Promise<any>>}
 */
const productsByHandle = new Map();

/**
 * Fetch a product as the Ajax API describes it for the shopper's market.
 *
 * @param {string} handle
 * @returns {Promise<any>} Null where the request failed.
 */
const fetchProduct = (handle) => {
  const held = productsByHandle.get(handle);
  if (held) return held;

  const root = /** @type {any} */ (window).Shopify?.routes?.root ?? '/';
  const request = fetch(`${root}products/${handle}.js`)
    .then((response) => {
      if (!response.ok) throw new Error(`Product ${handle}: ${response.status}`);

      return response.json();
    })
    .catch((error) => {
      console.error(error);
      productsByHandle.delete(handle);

      return null;
    });

  productsByHandle.set(handle, request);

  return request;
};

/**
 * What the shopper is being charged for one variant, in the market's currency.
 *
 * The Athos feed quotes the shop's currency and knows nothing of a market's tax treatment, so its
 * price is only a fallback: measured against the storefront, `price * Shopify.currency.rate` matches
 * in CAD, AUD and SGD but under-reports every tax-inclusive market by its VAT rate — 26.98 GBP
 * charged against 22.48 converted. The Ajax API answers for the market the shopper is actually in.
 *
 * @param {any} result - An Athos result.
 * @param {any} variant - Its `ss_option_sizes` entry for the size being tagged.
 * @param {string} currency
 * @returns {Promise<number>}
 */
const fetchMarketPrice = async (result, variant, currency) => {
  const precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;
  const factor = 10 ** precision;

  const handle = productHandleFromUrl(result.url);
  const wanted = String(variant?.variant_id ?? result.uid ?? '');
  const product = handle ? await fetchProduct(handle) : null;
  const priced = product?.variants?.find((/** @type {any} */ entry) => String(entry.id) === wanted);

  if (typeof priced?.price === 'number') return priced.price / factor;

  const fallback = Number(variant?.price ?? result.price);

  return Number.isFinite(fallback) ? Math.round(convertMoney(fallback) * factor) / factor : 0;
};

/**
 * Describe one garment for EdgeTag, identified by the variant in the shopper's size. An Athos result
 * describes one variant rather than a product, so the product id is `groupId` and the result's own
 * `sku` and `price` are that variant's.
 *
 * @param {any} result - An Athos result.
 * @param {string} [size]
 * @param {string} currency - The market's currency, which the price is read in.
 * @returns {Promise<object>}
 */
const buildProductPayload = async (result, size, currency) => {
  // Where the shopper has picked no size the event still names a variant: the first one they could
  // buy, or the first of any where the print is sold out throughout.
  // `result.sku` is no fallback: it names the size the query filtered on, not the one picked.
  const variants = readVariants(result);
  const variant =
    findVariantBySize(result, size) ?? variants.find((/** @type {any} */ entry) => entry.available) ?? variants[0];
  const price = await fetchMarketPrice(result, variant, currency);

  return {
    id: String(result.groupId ?? ''),
    variantId: String(variant?.variant_id ?? ''),
    sku: String(variant?.sku ?? ''),
    quantity: 1,
    item_price: price,
    title: decodeEntities(String(result.name ?? '')),
    image: String(result.image ?? ''),
    brand: String(result.vendor ?? ''),
    category: String(result.product_type ?? ''),
    url: result.url ? new URL(result.url, window.location.origin).href : '',
    type: CONTENT_TYPE,
  };
};

document.addEventListener(PRINT_SWATCH_UPDATE, async (event) => {
  const selector = event.target;
  if (!(selector instanceof HTMLElement)) return;

  const result = /** @type {CustomEvent} */ (event).detail;
  const garments = result?.matches?.length ? result.matches : [result];
  if (!garments[0]) return;

  // A print of several garments arrives with them already fetched, so pricing them costs nothing.
  for (const product of result.products ?? []) {
    if (product?.handle) productsByHandle.set(product.handle, Promise.resolve(product));
  }

  const currency = /** @type {any} */ (window).Shopify?.currency?.active ?? '';
  const sizes = readSizesByType(selector);
  const contents = await Promise.all(
    garments.map((/** @type {any} */ garment) =>
      buildProductPayload(garment, sizes.get(garment.product_type) ?? selector.dataset.size, currency),
    ),
  );

  const value = contents.reduce(
    (/** @type {number} */ total, /** @type {any} */ content) => total + content.item_price * content.quantity,
    0,
  );

  tagEvent('ViewContent', { currency, value, contents });
});
